import React, { FC } from 'react';
import styled from 'styled-components';
import { SkeletonBlock, SkeletonPageShell, SkeletonResponsiveGrid, SkeletonRow, SkeletonStack, SkeletonSurface } from 'src/components/widgets/skeleton';
import { SettingsFormPageSkeleton } from '../settings/settingsSkeleton';

const PricingSkeletonShell = styled(SkeletonPageShell)`
    padding: 0;
    background: #fafbfb;
`;

const PricingSkeletonTopbar = styled.div`
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr) 120px;
    align-items: center;
    gap: 24px;
    padding: 18px 48px;
    background: #ffffff;

    @media (max-width: 700px) {
        grid-template-columns: 1fr 1fr;
        padding: 16px 24px;
    }
`;

const PricingSkeletonLogo = styled(SkeletonBlock)`
    justify-self: center;

    @media (max-width: 700px) {
        display: none;
    }
`;

const PricingSkeletonSection = styled.div`
    max-width: 1266px;
    margin: 0 auto;
    padding: 44px 48px 56px;

    @media (max-width: 700px) {
        padding: 32px 24px 40px;
    }
`;

const PricingSkeletonHeading = styled(SkeletonStack)`
    align-items: center;
    margin-bottom: 30px;
`;

const PricingSkeletonControls = styled(SkeletonRow)`
    justify-content: center;
    position: relative;
    margin-bottom: 54px;

    @media (max-width: 1024px) {
        flex-direction: column;
        margin-bottom: 24px;
    }
`;

const PricingSkeletonCurrency = styled(SkeletonBlock)`
    position: absolute;
    right: 0;

    @media (max-width: 1024px) {
        position: static;
    }
`;

const PricingSkeletonPlanCard = styled(SkeletonSurface)`
    min-height: 520px;
    padding: 24px;
    border-radius: 12px;
`;

const PricingSkeletonFeatureList = styled(SkeletonStack)`
    margin-top: 28px;
`;

const PricingSkeletonFaq = styled.div`
    background: #f1f2f3;
    padding: 44px 48px;

    @media (max-width: 700px) {
        padding: 32px 24px;
    }
`;

const PricingSkeletonFaqInner = styled.div`
    max-width: 990px;
    margin: 0 auto;
`;

const PricingSkeletonFaqItem = styled(SkeletonSurface)`
    padding: 18px 22px;
    border-radius: 8px;
`;

const PricingPlanCardSkeleton: FC<{ index: number }> = ({ index }) => (
    <PricingSkeletonPlanCard>
        <SkeletonStack $gap='14px'>
            <SkeletonBlock $width={index === 1 ? '150px' : '120px'} $height='24px' />
            <SkeletonBlock $width='min(240px, 100%)' $height='13px' />
        </SkeletonStack>
        <SkeletonStack $gap='10px' $margin='34px 0 0'>
            <SkeletonBlock $width='110px' $height='42px' />
            <SkeletonBlock $width='160px' $height='13px' />
        </SkeletonStack>
        <SkeletonBlock $width='100%' $height='42px' $radius='8px' $margin='28px 0 0' />
        <PricingSkeletonFeatureList $gap='14px'>
            {Array.from({ length: 8 }).map((_, featureIndex) => (
                <SkeletonRow key={featureIndex} $gap='10px'>
                    <SkeletonBlock $width='16px' $height='16px' $round />
                    <SkeletonBlock $width={featureIndex % 3 === 0 ? '88%' : featureIndex % 3 === 1 ? '72%' : '58%'} $height='13px' />
                </SkeletonRow>
            ))}
        </PricingSkeletonFeatureList>
    </PricingSkeletonPlanCard>
);

export const ChoosePlanPageSkeleton: FC = () => (
    <PricingSkeletonShell aria-hidden='true'>
        <PricingSkeletonTopbar>
            <SkeletonBlock $width='72px' $height='34px' $radius='6px' />
            <PricingSkeletonLogo $width='120px' $height='28px' />
            <SkeletonBlock $width='110px' $height='34px' $radius='6px' />
        </PricingSkeletonTopbar>
        <PricingSkeletonSection>
            <PricingSkeletonHeading $gap='14px'>
                <SkeletonBlock $width='min(520px, 100%)' $height='42px' />
                <SkeletonBlock $width='min(420px, 90%)' $height='16px' />
            </PricingSkeletonHeading>
            <PricingSkeletonControls $gap='16px'>
                <SkeletonBlock $width='210px' $height='36px' $radius='18px' />
                <PricingSkeletonCurrency $width='150px' $height='38px' $radius='6px' />
            </PricingSkeletonControls>
            <SkeletonResponsiveGrid $columns='repeat(4, minmax(0, 1fr))' $tabletColumns='repeat(2, minmax(0, 1fr))' $mobileColumns='minmax(0, 1fr)'>
                {Array.from({ length: 4 }).map((_, index) => (
                    <PricingPlanCardSkeleton key={index} index={index} />
                ))}
            </SkeletonResponsiveGrid>
        </PricingSkeletonSection>
        <PricingSkeletonFaq>
            <PricingSkeletonFaqInner>
                <SkeletonStack $gap='18px' $align='center'>
                    <SkeletonBlock $width='min(360px, 100%)' $height='34px' />
                    {Array.from({ length: 4 }).map((_, index) => (
                        <PricingSkeletonFaqItem key={index}>
                            <SkeletonBlock $width={index % 2 === 0 ? '72%' : '58%'} $height='16px' />
                        </PricingSkeletonFaqItem>
                    ))}
                </SkeletonStack>
            </PricingSkeletonFaqInner>
        </PricingSkeletonFaq>
    </PricingSkeletonShell>
);

export const SubscriptionStartPageSkeleton: FC = () => <SettingsFormPageSkeleton />;
